import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import SlideOutSidebar from "../../../ui-components/slide-out-sidebar/slideOutSidebar";
import "./terminalActivitylog.scss";
import Button, { ButtonThemes } from "../../../ui-components/button/button";
import LabeledTextInput from "../../../ui-components/molecules/labeled-inputs/labeled-text-input/labeledTextInput";
import LabeledSelectInput from "../../../ui-components/molecules/labeled-inputs/labeled-select-input/labeledSelectInput";
import LabeledDateRangeInput from "../../../ui-components/molecules/labeled-inputs/labeled-date-range-input/labeledDateRangeInput";
import { LabelModes } from "../../../ui-components/molecules/labeled-inputs/labeledInput";
import Table from "../../../ui-components/table/table";
import { XGSSelectOption } from "../../../ui-components/xgs-select/xgsSelect";
import {
  terminalActivitylogSelector,
  getTerminalActivityLogList,
  STATIC_ACTIVITY_LOG_ACTION_OPTIONS,
} from "../../../slices/terminal-summary/activityLogSlice";
import TerminalActivitylogState from "../../../slices/terminal-summary/ActivityLogState";
import getTerminalActivityLogColumns from "./terminalActivityColumns";

export interface TeamDetailsProps {
  show: boolean;
  onClose: () => void;
}

const TerminalActivityLog: React.FC<TeamDetailsProps> = (props) => {
  const dispatch = useDispatch();
  const terminalActivitylogState: TerminalActivitylogState = useSelector(terminalActivitylogSelector);
  const [fromDate, setFromDate] = useState<string>("");
  const [toDate, setToDate] = useState<string>("");
  const [search, setSearch] = useState<string>("");
  const [actionType, setActionType] = useState<XGSSelectOption | null | undefined>(null);

  const formatDate = (dateStr: string | null | undefined): string => {
    if (!dateStr) return "";

    const parts = dateStr.split("/");
    if (parts.length !== 3) return "";

    const [month, day, year] = parts;
    return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
  };

  const diffsAfterSplitting = useMemo(() => {
    let diffs: {
      changedAt: string;
      terminalName: string;
      action: string;
      modifiedBy: string;
      changeDescription: string;
      terminalNumber?: string | number | null; 
    }[] = [];

    if (!terminalActivitylogState.terminalActivityLogs) return [];

    terminalActivitylogState.terminalActivityLogs.forEach((item) => {
      const lines = item.changeDescription.split("\n").filter(line => line.trim() !== "");

      lines.forEach((line) => {
        const updatedLine = line.replaceAll("-->", "→");
      
        // find ISO date + [UTC] in the line
        const formattedLine = updatedLine.replace(
          /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}Z\[UTC\]/g,
          (match) => {
            const clean = match.replace(/\[.*\]/, ""); // remove [UTC]
            return clean.toUiMonthDateFormat();
          }
        );
      
        diffs.push({
          changedAt: item.changedAt,
          terminalName: `${item.airportCode || ""} - ${item.terminalName || ""}`,
          action: item.action,
          modifiedBy: item.modifiedBy,
          changeDescription: formattedLine,
          terminalNumber: item.terminalNumber,
        });
      });
    });
    return diffs;
  }, [terminalActivitylogState.terminalActivityLogs]);

  const getActionOptions = () => {
    return (
      STATIC_ACTIVITY_LOG_ACTION_OPTIONS.map(
        (actions): XGSSelectOption => ({
          label: actions.name,
          value: actions.id,
        })
      ) || []
    );
  };

  useEffect(() => {
    dispatch(getTerminalActivityLogList(0));
  }, [dispatch]);

  const onInfiniteScroll = () => {
    if (terminalActivitylogState.terminalActivityLogsRequest.last) return;
    const nextPage = terminalActivitylogState.terminalActivityLogsRequest.page + 1;
    dispatch(getTerminalActivityLogList(nextPage, search, actionType?.label ?? null, fromDate, toDate));
  };

  const onFromDateChanged = (value: string) => {
    setFromDate(formatDate(value));
  };

  const onToDateChanged = (value: string) => {
    setToDate(formatDate(value));
  };

  const onActionChanged = (_actiontype: XGSSelectOption | null | undefined) => {
    if (!_actiontype) return;
    setActionType(_actiontype);
    // dispatch(getTerminalActivityLogList(0, search, _actiontype?.label, formatDate(fromDate) ?? null, formatDate(toDate) ?? null));
  };

  const searchRecords = () => {
    if ((search || actionType?.label || fromDate || toDate) !== "" || null || undefined) {
      dispatch(getTerminalActivityLogList(0, search, actionType?.label ?? null, fromDate ?? null, toDate ?? null));
    }
  };

  const clearSearchRecords = () => {
    setFromDate("");
    setToDate("");
    setSearch("");
    setSearch("");
    setActionType(null);
    dispatch(getTerminalActivityLogList(0));
  };

  return (
    <SlideOutSidebar
      header="Terminal Activity Log"
      onClose={props.onClose}
      show={props.show}
      size="medium"
      className="xgs-activitylog __details"
    >
      <>
        <div className="xgs-list__controls">
          <div className="xgs-list__controls__search xgs-activitylog__list__filters">
            <LabeledTextInput
              label="Search Terminal"
              labelMode={LabelModes.column}
              className="xgs-activitylog__list__filters__input"
              value={search}
              onChange={(e) => setSearch(e.currentTarget.value)}
              // onKeyDown={(e) => (e.key === "Enter") && searchRecords()}
              type="text"
              placeholder="Search across all fields"
            />
            <LabeledDateRangeInput
              label="Dates range:"
              labelMode={LabelModes.column}
              className="xgs-activitylog__list__filters__dates"
              start={fromDate}
              end={toDate}
              onStartChange={onFromDateChanged}
              onEndChange={onToDateChanged}
            />
            <LabeledSelectInput
              label="Action"
              onValueChange={onActionChanged}
              name="actionType"
              value={actionType}
              className="xgs-activitylog__list__filters__input"
              options={getActionOptions()}
              labelMode={LabelModes.column}
              placeholder="Action Type"
            />
            <Button
              className="xgs-activitylog__list__filters__button"
              theme={ButtonThemes.gray}
              onClick={clearSearchRecords}
            >
              Clear Filters
            </Button>
            <Button
              className="xgs-activitylog__list__filters__button xgs-activitylog__list__filters__button--last"
              theme={ButtonThemes.blue}
              onClick={searchRecords}
            >
              Search
            </Button>
          </div>
        </div>
        <div className="xgs-activitylog__details__table-wrapper">
          {!terminalActivitylogState.loadingFailed && <Table
            isLoading={terminalActivitylogState.requestStarted || (terminalActivitylogState.fetchPortionStarted && terminalActivitylogState.requestCreator === "GET_ACTIVITY_LOGS")}
            columns={getTerminalActivityLogColumns()}
            data={diffsAfterSplitting}
            rowHeight={50}
            minTableHeight={200}
            noResultsText="There are no logs available"
            infiniteScroll
            infiniteScrollLoading={terminalActivitylogState.fetchPortionStarted && terminalActivitylogState.requestCreator === "GET_ACTIVITY_LOGS_PORTION"}
            infiniteScrollHasNext={!terminalActivitylogState.terminalActivityLogsRequest.last}
            onInfiniteScroll={onInfiniteScroll}
            sorting={true}
            enableMultiSort={true}
          />}
        </div>
      </>
    </SlideOutSidebar>
  );
};

export default TerminalActivityLog;
